import React from 'react';
import Select from 'react-select';
import env from '../../environment.js';

const RIESGO_OPTIONS = [
  { value: 1, label: 'Bajo' },
  { value: 2, label: 'Medio' },
  { value: 3, label: 'Alto' },
];

const SIN_ASIGNAR_OPTION = { value: null, label: 'Sin asignar' };

// Colores exactos tomados vía Figma REST API del componente Filter/Dropdown
// del design system (Design System - Sirio App Desktop, node 36:547):
// - Trigger: borde 1px #B1B1B1 (default) / 1px #5F5F59 (hover) / 2px #86B900
//   (focus) / 1px #E9E9E9 (disabled). Se conserva el relleno sólido de color
//   de este selector (no el fondo blanco del Figma) — solo el borde sigue el
//   patrón del sistema, no el fondo.
// - Filter/Dropdown Menu (el contenedor de opciones): SIEMPRE borde 1px
//   #B1B1B1 + sombra — nunca el verde del foco. El "mismo stroke y radius
//   que el trigger" de la nota de Figma es el radius (aquí 6px, igual que el
//   trigger), no el color de borde del estado enfocado.
// - Opciones del menú: fondo transparente en reposo, #ECF2E0 en hover,
//   #4F7A00 con texto blanco cuando está seleccionada.
const TRIGGER_BORDER = { default: '#B1B1B1', hover: '#5F5F59', focus: '#86B900', disabled: '#E9E9E9' };
const MENU_BORDER = '#B1B1B1';
const OPTION_HOVER_BG = '#ECF2E0';
const OPTION_SELECTED_BG = '#4F7A00';

const pillStyles = color => ({
  container: base => ({ ...base, minWidth: 100 }),
  control: (base, state) => ({
    ...base,
    minHeight: 0,
    backgroundColor: color || env.colors.neutral_300,
    border: `${state.isFocused ? 2 : 1}px solid ${
      state.isFocused ? TRIGGER_BORDER.focus : state.isDisabled ? TRIGGER_BORDER.disabled : 'transparent'
    }`,
    borderRadius: 6,
    boxShadow: 'none',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    opacity: state.isDisabled ? 0.5 : 1,
    flexWrap: 'nowrap',
    transition: 'border-color 120ms ease',
    '&:hover': {
      borderColor: state.isFocused || state.isDisabled ? undefined : TRIGGER_BORDER.hover,
    },
  }),
  valueContainer: base => ({ ...base, padding: '5px 0 5px 10px', flexWrap: 'nowrap' }),
  singleValue: base => ({ ...base, color: '#fff', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap' }),
  placeholder: base => ({ ...base, color: '#fff', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap' }),
  input: base => ({ ...base, color: '#fff', margin: 0, padding: 0 }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: base => ({ ...base, color: '#fff', padding: '0 8px 0 0' }),
  indicatorsContainer: base => ({ ...base, height: '100%' }),
  menu: base => ({
    ...base,
    minWidth: 90,
    fontSize: 12,
    border: `1px solid ${MENU_BORDER}`,
    borderRadius: 6,
    boxShadow: '0 4px 12px rgba(0,0,0,0.14)',
  }),
  menuPortal: base => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? OPTION_SELECTED_BG : state.isFocused ? OPTION_HOVER_BG : 'transparent',
    color: state.isSelected ? '#fff' : env.colors.neutral_900,
    cursor: 'pointer',
    // react-select inyecta su propio `&:active` azul del tema por separado
    // del `styles` prop (queda en una regla CSS aparte con la misma
    // especificidad) — sin !important, cuál de las dos gana depende del
    // orden de inserción y no es confiable, y se ve un flash azul al hacer
    // click entre el hover verde tenue y el verde fuerte de seleccionado.
    '&:active': { backgroundColor: `${OPTION_SELECTED_BG} !important`, color: '#fff !important' },
  }),
});

// react-select pintado como la pastilla sólida de color del Figma — sin
// buscador/escritura (isSearchable=false). "Sin asignar" es el placeholder
// cuando no hay valor; solo se agrega como opción elegible cuando ya hay un
// riesgo asignado, para poder desasignarlo (si no hubiera valor, ya está en
// "sin asignar" y la opción sería redundante).
const SelectRiesgo = ({ value, color, onChange, disabled = false }) => {
  const selected = RIESGO_OPTIONS.find(opt => opt.value === value) || null;
  const options = value ? [SIN_ASIGNAR_OPTION, ...RIESGO_OPTIONS] : RIESGO_OPTIONS;

  const handleChange = option => {
    onChange({ target: { value: option.value } });
  };

  return (
    <Select
      aria-label="Nivel de riesgo"
      value={selected}
      onChange={handleChange}
      options={options}
      placeholder="Sin asignar"
      isSearchable={false}
      isClearable={false}
      isDisabled={disabled}
      menuPortalTarget={document.body}
      styles={pillStyles(color)}
    />
  );
};

export default SelectRiesgo;
